import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import axios from 'axios';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Check, Envelope, MapPin, Plus, X, Users } from '@phosphor-icons/react';

const API = '/api';

const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
  'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
  'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
  'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
  'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
  'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
  'Wisconsin', 'Wyoming', 'District of Columbia'
];

export default function SaveTheDatePage() {
  const [step, setStep] = useState('form'); // form, success
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedState, setSelectedState] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('United States');
  const [plusOnes, setPlusOnes] = useState([]);
  
  const { register, handleSubmit, formState: { errors } } = useForm();

  const addPlusOne = () => {
    setPlusOnes([...plusOnes, { name: '', email: '', phone: '' }]);
  };

  const removePlusOne = (index) => {
    setPlusOnes(plusOnes.filter((_, i) => i !== index));
  };

  const updatePlusOne = (index, field, value) => {
    const updated = [...plusOnes];
    updated[index][field] = value;
    setPlusOnes(updated);
  };

  const onSubmit = async (data) => {
    if (!selectedState && selectedCountry === 'United States') {
      toast.error('Please select your state / Por favor seleccione su estado');
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        first_name: data.first_name,
        last_name: data.last_name,
        email: data.email,
        phone: data.phone || null,
        address_line1: data.address_line1,
        address_line2: data.address_line2 || null,
        city: data.city,
        state: selectedState || data.state,
        zip_code: data.zip_code,
        country: selectedCountry,
        plus_ones: plusOnes.filter(po => po.name.trim() !== ''),
      };

      await axios.post(`${API}/addresses`, payload);
      setStep('success');
      toast.success('Thank you! Your address has been saved.');
    } catch (error) {
      console.error('Address submission error:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Success Screen
  if (step === 'success') {
    return (
      <div data-testid="address-success" className="min-h-screen flex items-center justify-center px-4 bg-white">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: '#F0EBE3' }}>
            <Check size={32} weight="bold" style={{ color: '#B8A88A' }} />
          </div>
          <h1 className="font-display text-3xl text-stone-900 mb-2">
            Thank You!
          </h1>
          <p className="font-body text-sm italic text-stone-500 mb-4">
            ¡Gracias!
          </p>
          <p className="font-body text-stone-600 mb-2">
            We've saved your information.
          </p>
          <p className="font-body text-sm italic text-stone-500 mb-6">
            Hemos guardado tu información.
          </p>
          <p className="font-display text-xl italic" style={{ color: '#B8A88A' }}>
            — Stefanie & Walter
          </p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="address-page" className="min-h-screen bg-white">
      {/* Hero Photo - Circular Image */}
      <div className="w-full bg-white pt-8 flex justify-center">
        <img
          src="https://customer-assets.emergentagent.com/job_2a6383c9-47ae-4f9d-80c4-df64c0760867/artifacts/qz8lgnbp_closes%20up.jpg"
          alt="Stefanie and Walter"
          className="w-48 h-48 md:w-56 md:h-56 rounded-full object-cover shadow-lg"
          style={{ border: '4px solid #F0EBE3' }}
        />
      </div>

      {/* Header */}
      <div className="py-8 text-center bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <h1 className="font-display text-4xl md:text-5xl mb-2" style={{ color: '#C9B99A' }}>
            We're Getting Married!
          </h1>
          <p className="font-body text-sm italic text-stone-500 mb-4">
            ¡Nos Casamos!
          </p>
          <p className="font-body text-stone-600">
            Stefanie & Walter would like to send something special your way—please share your contact details below.
          </p>
          <p className="font-body text-sm italic text-stone-500 mt-2">
            Stefanie y Walter desean enviarte algo especial—por favor comparte tus datos de contacto.
          </p>
        </div>
      </div>

      {/* Address Form */}
      <div className="py-8 md:py-12 bg-white">
        <div className="max-w-xl mx-auto px-4">
          <div className="bg-white border border-stone-200 p-8">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: '#F0EBE3' }}>
                <Envelope size={20} style={{ color: '#B8A88A' }} />
              </div>
              <div>
                <h2 className="font-display text-xl text-stone-900">Your Information</h2>
                <p className="font-body text-xs italic text-stone-500">Tu Información</p>
              </div>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {/* Name Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name" className="font-body text-sm text-stone-700">
                    First Name * <span className="italic text-stone-400 text-xs">/ Nombre</span>
                  </Label>
                  <Input
                    id="first_name"
                    data-testid="input-first-name"
                    {...register('first_name', { required: 'First name is required' })}
                    className="bg-white border-stone-200 focus:border-[#C9B99A]"
                    placeholder="Your first name"
                  />
                  {errors.first_name && (
                    <p className="text-red-500 text-xs">{errors.first_name.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="last_name" className="font-body text-sm text-stone-700">
                    Last Name * <span className="italic text-stone-400 text-xs">/ Apellido</span>
                  </Label>
                  <Input
                    id="last_name"
                    data-testid="input-last-name"
                    {...register('last_name', { required: 'Last name is required' })}
                    className="bg-white border-stone-200 focus:border-[#C9B99A]"
                    placeholder="Your last name"
                  />
                  {errors.last_name && (
                    <p className="text-red-500 text-xs">{errors.last_name.message}</p>
                  )}
                </div>
              </div>

              {/* Email & Phone */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="font-body text-sm text-stone-700">
                    Email * <span className="italic text-stone-400 text-xs">/ Correo Electrónico</span>
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="input-email"
                    {...register('email', { 
                      required: 'Email is required',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Invalid email address'
                      }
                    })}
                    className="bg-white border-stone-200 focus:border-[#C9B99A]"
                    placeholder="your@email.com"
                  />
                  {errors.email && (
                    <p className="text-red-500 text-xs">{errors.email.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone" className="font-body text-sm text-stone-700">
                    Phone * <span className="italic text-stone-400 text-xs">/ Teléfono</span>
                  </Label>
                  <Input
                    id="phone"
                    data-testid="input-phone"
                    {...register('phone', { required: 'Phone number is required' })}
                    className="bg-white border-stone-200 focus:border-[#C9B99A]"
                    placeholder="(555) 123-4567"
                  />
                  {errors.phone && (
                    <p className="text-red-500 text-xs">{errors.phone.message}</p>
                  )}
                </div>
              </div>

              {/* Address Section */}
              <div className="pt-6 border-t border-stone-100">
                <div className="flex items-center gap-2 mb-4">
                  <MapPin size={18} style={{ color: '#B8A88A' }} />
                  <Label className="font-body text-sm font-medium text-stone-700">
                    Mailing Address <span className="italic text-stone-400 text-xs font-normal">/ Dirección Postal</span>
                  </Label>
                </div>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="address_line1" className="font-body text-sm text-stone-700">
                      Street Address * <span className="italic text-stone-400 text-xs">/ Dirección</span>
                    </Label>
                    <Input
                      id="address_line1"
                      data-testid="input-address1"
                      {...register('address_line1', { required: 'Address is required' })}
                      className="bg-white border-stone-200 focus:border-[#C9B99A]"
                      placeholder="123 Main Street"
                    />
                    {errors.address_line1 && (
                      <p className="text-red-500 text-xs">{errors.address_line1.message}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="address_line2" className="font-body text-sm text-stone-700">
                      Apt, Suite, Unit <span className="italic text-stone-400 text-xs">/ Apto (Opcional)</span>
                    </Label>
                    <Input
                      id="address_line2"
                      data-testid="input-address2"
                      {...register('address_line2')}
                      className="bg-white border-stone-200 focus:border-[#C9B99A]"
                      placeholder="Apt 4B"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="city" className="font-body text-sm text-stone-700">
                        City * <span className="italic text-stone-400 text-xs">/ Ciudad</span>
                      </Label>
                      <Input
                        id="city"
                        data-testid="input-city"
                        {...register('city', { required: 'City is required' })}
                        className="bg-white border-stone-200 focus:border-[#C9B99A]"
                        placeholder="New York"
                      />
                      {errors.city && (
                        <p className="text-red-500 text-xs">{errors.city.message}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label className="font-body text-sm text-stone-700">
                        State * <span className="italic text-stone-400 text-xs">/ Estado</span>
                      </Label>
                      <Select value={selectedState} onValueChange={setSelectedState}>
                        <SelectTrigger data-testid="select-state" className="bg-white border-stone-200 focus:border-[#C9B99A]">
                          <SelectValue placeholder="Select state" />
                        </SelectTrigger>
                        <SelectContent>
                          {US_STATES.map((state) => (
                            <SelectItem key={state} value={state}>
                              {state}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="zip_code" className="font-body text-sm text-stone-700">
                        ZIP Code * <span className="italic text-stone-400 text-xs">/ Código Postal</span>
                      </Label>
                      <Input
                        id="zip_code"
                        data-testid="input-zip"
                        {...register('zip_code', { required: 'ZIP code is required' })}
                        className="bg-white border-stone-200 focus:border-[#C9B99A]"
                        placeholder="10001"
                      />
                      {errors.zip_code && (
                        <p className="text-red-500 text-xs">{errors.zip_code.message}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label className="font-body text-sm text-stone-700">
                        Country <span className="italic text-stone-400 text-xs">/ País</span>
                      </Label>
                      <Select value={selectedCountry} onValueChange={setSelectedCountry}>
                        <SelectTrigger data-testid="select-country" className="bg-white border-stone-200 focus:border-[#C9B99A]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="United States">United States</SelectItem>
                          <SelectItem value="Canada">Canada</SelectItem>
                          <SelectItem value="Mexico">Mexico / México</SelectItem>
                          <SelectItem value="El Salvador">El Salvador</SelectItem>
                          <SelectItem value="Other">Other / Otro</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Plus Ones Section - Open for anyone */}
              <div className="pt-6 border-t border-stone-100">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Users size={18} style={{ color: '#B8A88A' }} />
                    <div>
                      <Label className="font-body text-sm font-medium text-stone-700">
                        Additional Household Members
                      </Label>
                      <p className="font-body text-xs italic text-stone-400">Miembros Adicionales del Hogar</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={addPlusOne}
                    className="flex items-center gap-1 text-sm font-body transition-colors"
                    style={{ color: '#B8A88A' }}
                  >
                    <Plus size={16} />
                    <span>Add <span className="italic text-xs">/ Agregar</span></span>
                  </button>
                </div>

                {plusOnes.length === 0 && (
                  <div>
                    <p className="font-body text-sm text-stone-400">
                      Add anyone else living at this address who should receive mail.
                    </p>
                    <p className="font-body text-xs italic text-stone-400 mt-1">
                      Agregue a cualquier otra persona que viva en esta dirección y deba recibir correo.
                    </p>
                  </div>
                )}

                <div className="space-y-4">
                  {plusOnes.map((plusOne, index) => (
                    <div key={index} className="bg-stone-50 border border-stone-200 p-4 space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="font-body text-sm font-medium text-stone-700">
                          Person {index + 1} <span className="italic text-stone-400 text-xs font-normal">/ Persona {index + 1}</span>
                        </span>
                        <button
                          type="button"
                          onClick={() => removePlusOne(index)}
                          className="text-stone-400 hover:text-red-500"
                        >
                          <X size={18} />
                        </button>
                      </div>
                      
                      <div className="space-y-2">
                        <Label className="font-body text-xs text-stone-500">
                          Full Name * <span className="italic">/ Nombre Completo</span>
                        </Label>
                        <Input
                          data-testid={`plus-one-name-${index}`}
                          value={plusOne.name}
                          onChange={(e) => updatePlusOne(index, 'name', e.target.value)}
                          className="bg-white border-stone-200 focus:border-[#C9B99A]"
                          placeholder="Guest's full name"
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="font-body text-xs text-stone-500">
                            Email <span className="italic">/ Correo (Opcional)</span>
                          </Label>
                          <Input
                            data-testid={`plus-one-email-${index}`}
                            type="email"
                            value={plusOne.email}
                            onChange={(e) => updatePlusOne(index, 'email', e.target.value)}
                            className="bg-white border-stone-200 focus:border-[#C9B99A]"
                            placeholder="guest@email.com"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="font-body text-xs text-stone-500">
                            Phone <span className="italic">/ Teléfono (Opcional)</span>
                          </Label>
                          <Input
                            data-testid={`plus-one-phone-${index}`}
                            value={plusOne.phone}
                            onChange={(e) => updatePlusOne(index, 'phone', e.target.value)}
                            className="bg-white border-stone-200 focus:border-[#C9B99A]"
                            placeholder="(555) 123-4567"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                data-testid="submit-address-btn"
                disabled={isSubmitting}
                className="w-full py-6 text-white font-body font-medium tracking-wide transition-all"
                style={{ backgroundColor: '#C9B99A' }}
              >
                {isSubmitting ? 'Submitting... / Enviando...' : 'Submit Address / Enviar Dirección'}
              </Button>
            </form>
          </div>

          {/* Note */}
          <div className="text-center mt-6">
            <p className="font-body text-sm text-stone-400">
              Your information will only be used to send wedding-related correspondence.
            </p>
            <p className="font-body text-xs italic text-stone-400 mt-1">
              Tu información solo se usará para enviar correspondencia relacionada con la boda.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
